import { TaskCard, QuestGroup, Reward } from '../types';

/**
 * Адаптер для преобразования данных заданий из API в формат, используемый в приложении
 */
export class TasksAdapter {
  /**
   * Преобразует данные из API в формат для TasksContainer и QuestCarousel
   * @param data Данные, полученные из API
   * @returns Объект с отфильтрованными и преобразованными данными
   */
  static adaptTasks(data: any): {
    individualTasks: TaskCard[];
    combinedTasks: QuestGroup[];
  } {
    // Проверяем новый формат данных
    if (data && typeof data === 'object' && 'individual' in data && 'combined' in data) {
      // Обрабатываем новый формат данных
      const individualGroups = data.individual || [];
      const combinedGroups = data.combined || [];
      
      // Преобразуем индивидуальные группы заданий в формат TaskCard
      const adaptedIndividualTasks: TaskCard[] = [];
      
      individualGroups.forEach((group: any) => {
        // Добавляем группу как отдельную карточку задания
        adaptedIndividualTasks.push({
          id: group.task_group_id,
          type: group.slug as 'sponsors' | 'quest-daily' | 'partners',
          title: group.name,
          group: 'individual',
          image: group.icon_url,
          rewards: group.rewards?.map((reward: Reward) => ({
            type: reward.type,
            amount: reward.amount
          })) || [],
          progress: {
            current: group.completed_tasks || 0,
            total: group.total_tasks || 0
          },
          header: {
            banner: group.banner_url,
            title: group.name,
            description: group.description,
            centerIcon: group.icon_url
          },
          slug: group.slug
        });
      });
      
      // Преобразуем комбинированные задания (квесты) в формат QuestGroup
      const adaptedCombinedTasks = combinedGroups.map((quest: any): QuestGroup => ({
        id: quest.task_group_id,
        slug: quest.slug,
        type: quest.slug,
        group: 'combined',
        name: quest.name,
        icon_url: quest.icon_url,
        rewards: quest.rewards?.map((reward: Reward) => ({
          type: reward.type,
          amount: reward.amount
        })) || [],
        completed_tasks: quest.completed_tasks || 0,
        total_tasks: quest.total_tasks || 0,
        progress: {
          current: quest.completed_tasks || 0,
          total: quest.total_tasks || 0
        },
        is_active: quest.is_active,
        status: quest.status
      }));
      
      return {
        individualTasks: adaptedIndividualTasks,
        combinedTasks: adaptedCombinedTasks
      };
    } else {
      // Обрабатываем старый формат данных для обратной совместимости
      // Сначала преобразуем все задания, устанавливая group = type
      const transformedData = Array.isArray(data) ? data.map((task) => ({
        ...task,
        group: task.type
      })) : [];
      
      const individualTasks = transformedData.filter(task => task.type === "individual");
      const combinedTasks = transformedData.filter(task => task.type === "combined");
      
      // Преобразуем индивидуальные задания в формат TaskCard
      const adaptedIndividualTasks = individualTasks.map((task): TaskCard => ({
        id: task.id,
        type: task.slug,
        title: task.name,
        group: task.group,
        image: task.icon_url,
        rewards: task.rewards?.map((reward: Reward) => ({
          type: reward.type,
          amount: reward.amount
        })) || [],
        progress: {
          current: task.completed_tasks || 0,
          total: task.total_tasks || 0
        },
      }));
      
      // Преобразуем комбинированные задания (квесты) в формат QuestGroup
      const adaptedCombinedTasks = combinedTasks.map((quest): QuestGroup => ({
        id: quest.id,
        slug: quest.slug,
        type: quest.slug,
        group: quest.group,
        name: quest.name,
        icon_url: quest.icon_url,
        rewards: quest.rewards?.map((reward: Reward) => ({
          type: reward.type,
          amount: reward.amount
        })) || [],
        completed_tasks: quest.completed_tasks || 0,
        total_tasks: quest.total_tasks || 0,
        progress: {
          current: quest.completed_tasks || 0,
          total: quest.total_tasks || 0
        },
        
        is_active: quest.is_active,
        status: quest.status
      }));
      
      return {
        individualTasks: adaptedIndividualTasks,
        combinedTasks: adaptedCombinedTasks
      };
    }
  }
} 